import React, { useState, useEffect } from 'react';
import {
  Button, IconButton, useToast, Tooltip, Modal, ModalOverlay, ModalContent,
  ModalHeader, ModalBody, ModalCloseButton, useDisclosure,
  Table, Thead, Tbody, Tr, Th, Td, Flex, Text, Image, Box, Badge, Icon,
  VStack, HStack, Divider, Alert, AlertIcon, FormControl, FormLabel, Input, Grid, GridItem,
  SimpleGrid, Link, useBreakpointValue, Collapse
} from '@chakra-ui/react';
import { ExternalLinkIcon, ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { FaTruck, FaExclamationCircle, FaArrowLeft, FaCheck, FaCheckCircle, FaPrint, FaSyncAlt, FaShoppingCart, FaTrash, FaFilePdf, FaBarcode, FaCopy, FaUser, FaSave } from 'react-icons/fa';
import { api } from '../../services/api';
import { useConfirm } from '../ConfirmDialog';
import { produtosAtivos } from '../OcorrenciasItemVenda';

// Status em que a etiqueta ja nao serve mais e a venda precisa de outra
const STATUS_PERMITE_REENVIO = ['undelivered', 'delivered', 'posted', 'received', 'paused'];

/**
 * autoReenvio: abre direto no formulario de reenvio, sem o botao proprio. Usado pela tela
 * /melhor-envio, que ja tem a etiqueta na mao e so precisa do formulario.
 */
const GeradorEtiquetaMelhorEnvio = ({ venda, autoReenvio = false, onFechar, comTexto = false }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLoading, setIsLoading] = useState(false);
  const [gerandoId, setGerandoId] = useState(null);
  const [isActionLoading, setIsActionLoading] = useState(false);

  const [step, setStep] = useState(null);
  const [payloadObj, setPayloadObj] = useState(null);
  const [transportadoraCombinada, setTransportadoraCombinada] = useState(null);
  const [entregaConfirmadaManual, setEntregaConfirmadaManual] = useState(false);
  const [cotacoes, setCotacoes] = useState([]);
  const [showDestinatarioReview, setShowDestinatarioReview] = useState(false);

  const [etiquetaSalva, setEtiquetaSalva] = useState(venda.etiqueta_melhor_envio || null);
  const [errorMsg, setErrorMsg] = useState("");
  // Reenvio: gerar outra etiqueta para uma venda que ja teve uma (nao entregue, extraviada)
  const [modoReenvio, setModoReenvio] = useState(false);
  const [nfeEmUso, setNfeEmUso] = useState(false);
  const [ultimoServicoId, setUltimoServicoId] = useState(null);
  const [adicionandoCarrinhoId, setAdicionandoCarrinhoId] = useState(null);
  const [salvandoDimensoes, setSalvandoDimensoes] = useState(false);

  const isMobile = useBreakpointValue({ base: true, md: false });

  const toast = useToast();
  const { confirm, ConfirmPortal } = useConfirm();
  const vendaId = venda.id_taga || venda.id;

  // Trava de conferencia: 1=SUCESSO, 3=EMBALADO, 4=ENVIADO, 5=LIBERADO_RESSALVA
  const STATUS_CONFERENCIA_OK = [1, 3, 4, 5];
  const ativosConferencia = produtosAtivos(venda);
  const isConferido = ativosConferencia.length > 0
    && ativosConferencia.every(p => STATUS_CONFERENCIA_OK.includes(p.conferencia_status));

  const avisarConferenciaPendente = () => {
    toast({
      title: "Conferencia pendente",
      description: `A venda ${venda?.id ?? vendaId} precisa estar totalmente conferida antes de gerar etiqueta no Melhor Envio.`,
      status: "warning",
      duration: 5000,
      isClosable: true,
    });
  };
  const dbEmpresa = venda?.envio?.empresa || venda?.envio?.forma_entrega || '';
  const dbServico = venda?.envio?.modo_envio || venda?.envio?.servico_nome || '';
  const dbFull = `${dbEmpresa}${dbServico}`.toLowerCase().replace(/[^a-z0-9]/gi, '');
  // Só conta como "gerada" (verde) se realmente foi paga. Pendente sem paid_at = apenas no carrinho.
  const isNoCarrinho = !!etiquetaSalva
    && etiquetaSalva.status === 'pending'
    && !etiquetaSalva.paid_at
    && !etiquetaSalva.generated_at;
  const isGerada = !!etiquetaSalva
    && etiquetaSalva.status !== 'canceled'
    && !isNoCarrinho;

  useEffect(() => {
    if (venda.etiqueta_melhor_envio) {
      setEtiquetaSalva(venda.etiqueta_melhor_envio);
    }
  }, [venda]);

  // Chamado pela /melhor-envio: entra direto no formulario de reenvio
  useEffect(() => {
    if (autoReenvio) {
      handlePrepararDados(true);
    }
    // roda uma vez, quando a tela monta o componente ja em modo reenvio
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoReenvio]);

  const handleCloseModal = () => {
    onClose();
    setTimeout(() => {
      setStep(null);
      setErrorMsg("");
      setShowDestinatarioReview(false);
      onFechar?.();
    }, 300);
  };

  const handlePrepararDados = async (reenvio = false) => {
    if (!isGerada && !isConferido) {
      avisarConferenciaPendente();
      return;
    }
    setIsLoading(true);
    setErrorMsg("");
    setStep(null);
    try {
      const ehReenvio = reenvio === true;
      const response = await api.post('/melhor-envio/preparar-payload', { venda_id: vendaId, dimensoes_vazias: true, reenvio: ehReenvio });

      setModoReenvio(ehReenvio);
      setNfeEmUso(false);

      if (response.data.success) {
        if (response.data.ja_gerada && response.data.etiqueta?.status !== 'canceled') {
          setEtiquetaSalva(response.data.etiqueta);
          setStep('printed');
          onOpen();
        } else {
          const dataPayload = response.data.payload;
          if (dataPayload?.volumes) {
            dataPayload.volumes = dataPayload.volumes.map(v => ({
              ...v,
              _valor: formatarValorBR(v._valor)
            }));
          }
          setPayloadObj(dataPayload);
          setTransportadoraCombinada(response.data.transportadora_combinada || null);
          setEntregaConfirmadaManual(!!response.data.entrega_confirmada_manual);
          setStep('review');
          onOpen();
        }
      }
    } catch (error) {
      toast({ title: 'Erro ao preparar', description: error.response?.data?.error || 'Erro inesperado.', status: 'error', isClosable: true });
    } finally {
      setIsLoading(false);
    }
  };

  // Mantem valor ja formatado (salvo, com virgula) e formata numero cru; vazio continua vazio
  const formatarValorBR = (val) => {
    if (val === '' || val === null || val === undefined) return '';
    const s = String(val);
    if (s.includes(',')) return s;
    const n = Number(s);
    return isNaN(n) ? '' : n.toFixed(2).replace('.', ',');
  };

  // Formata numero da estimativa para exibicao (referencia read-only)
  const formatarEstimativa = (val) => {
    if (val === '' || val === null || val === undefined) return '-';
    const n = Number(String(val).replace(',', '.'));
    if (isNaN(n)) return '-';
    return Number.isInteger(n) ? String(n) : n.toFixed(2).replace('.', ',');
  };

  const handleSalvarDimensoes = async () => {
    setSalvandoDimensoes(true);
    try {
      await api.post('/melhor-envio/salvar-dimensoes', { venda_id: vendaId, volumes: payloadObj.volumes });
      toast({ title: 'Dimensoes salvas', status: 'success', duration: 3000, isClosable: true });
    } catch (error) {
      toast({ title: 'Erro ao salvar dimensoes', description: error.response?.data?.error || 'Erro inesperado.', status: 'error', isClosable: true });
    } finally {
      setSalvandoDimensoes(false);
    }
  };

  const recalcularSeguro = (volumes) => {
    return volumes.reduce((acc, v) => {
      const val = parseFloat(String(v._valor || '0').replace(',', '.'));
      return acc + (isNaN(val) ? 0 : val);
    }, 0);
  };

  const handleVolumeChange = (index, field, value) => {
    let formattedValue = value;

    if (field !== '_nome') {
      formattedValue = formattedValue.replace('.', ',');

      // Se for o campo de valor (seguro), limita a 2 casas decimais
      if (field === '_valor') {
        // Permite apenas números e no máximo uma vírgula com até 2 dígitos depois dela
        if (!/^\d*\,?\d{0,2}$/.test(formattedValue)) return;
      } else {
        // Para medidas e peso (permite mais de 2 casas, se necessário, mas barra letras)
        if (!/^\d*\,?\d*$/.test(formattedValue)) return;
      }
    }

    setPayloadObj(prev => {
      const newVolumes = [...prev.volumes];
      newVolumes[index] = {
        ...newVolumes[index],
        [field]: formattedValue
      };

      return {
        ...prev,
        volumes: newVolumes,
        options: { ...prev.options, insurance_value: recalcularSeguro(newVolumes) }
      };
    });
  };

  const handleAddVolume = () => {
    setPayloadObj(prev => {
      const newVolumes = [
        ...prev.volumes,
        { _nome: `Caixa ${prev.volumes.length + 1}`, height: '', width: '', length: '', weight: '', _qtd: 1, _valor: '' }
      ];
      return {
        ...prev,
        volumes: newVolumes,
        options: { ...prev.options, insurance_value: recalcularSeguro(newVolumes) }
      };
    });
  };

  const handleRemoveVolume = (index) => {
    if (payloadObj.volumes.length <= 1) return;
    setPayloadObj(prev => {
      const newVolumes = [...prev.volumes];
      newVolumes.splice(index, 1);
      return {
        ...prev,
        volumes: newVolumes,
        options: { ...prev.options, insurance_value: recalcularSeguro(newVolumes) }
      };
    });
  };

  const syncPayloadForApi = () => {
    const produtosSincronizados = payloadObj.volumes.map((vol, idx) => {
      const val = parseFloat(String(vol._valor || '0').replace(',', '.'));
      return {
        name: vol._nome || `Caixa ${idx + 1}`,
        quantity: vol._qtd || 1,
        unitary_value: isNaN(val) || val <= 0 ? 100 : Number(val.toFixed(2))
      };
    });

    const volumesFormatados = payloadObj.volumes.map(vol => {
      const val = parseFloat(String(vol._valor || '0').replace(',', '.'));
      return {
        ...vol,
        height: parseFloat(String(vol.height || '0').replace(',', '.')),
        width: parseFloat(String(vol.width || '0').replace(',', '.')),
        length: parseFloat(String(vol.length || '0').replace(',', '.')),
        weight: parseFloat(String(vol.weight || '0').replace(',', '.')),
        _valor: isNaN(val) || val <= 0 ? 100 : Number(val.toFixed(2))
      };
    });

    return {
      ...payloadObj,
      volumes: volumesFormatados,
      products: produtosSincronizados,
      options: {
        ...payloadObj.options,
        insurance_value: Number(recalcularSeguro(payloadObj.volumes).toFixed(2))
      }
    };
  };

  const handleCotarFrete = async () => {
    if (!payloadObj?.volumes || payloadObj.volumes.length === 0) {
      toast({ title: 'Aviso', description: 'É necessário ter pelo menos 1 pacote.', status: 'warning', isClosable: true });
      return;
    }

    for (let i = 0; i < payloadObj.volumes.length; i++) {
      const v = payloadObj.volumes[i];
      const w = parseFloat(String(v.weight || '0').replace(',', '.'));
      const h = parseFloat(String(v.height || '0').replace(',', '.'));
      const wi = parseFloat(String(v.width || '0').replace(',', '.'));
      const l = parseFloat(String(v.length || '0').replace(',', '.'));

      if (!v._nome || v._nome.trim() === '') {
        toast({ title: 'Aviso', description: `O nome do pacote ${i + 1} não pode estar vazio.`, status: 'warning', isClosable: true });
        return;
      }
      if (w <= 0 || h <= 0 || wi <= 0 || l <= 0) {
        toast({ title: 'Aviso', description: `As dimensões e o peso do pacote ${i + 1} devem ser maiores que zero.`, status: 'warning', isClosable: true });
        return;
      }
    }

    const payloadFormatado = syncPayloadForApi();

    setIsLoading(true);
    setErrorMsg("");
    try {
      const response = await api.post('/melhor-envio/cotar-venda', {
        venda_id: vendaId,
        custom_payload: payloadFormatado
      });

      if (response.data.success) {
        setCotacoes(response.data.data);
        setStep('quotes');
      }
    } catch (error) {
      setErrorMsg(error.response?.data?.error || 'Erro ao cotar o frete. Verifique o CEP.');
    } finally {
      setIsLoading(false);
    }
  };

  // Abre a tela de revisao de novo para gerar outra etiqueta na mesma venda. O backend tenta
  // cancelar a anterior; se ela ja foi postada a ME recusa e a chave da NF-e continua presa.
  const handleAbrirReenvio = async () => {
    const ok = await confirm({
      title: 'Criar nova etiqueta para esta venda?',
      body: `A etiqueta atual (${etiquetaSalva?.codigo_rastreio || 'sem rastreio'}) sera cancelada se o Melhor Envio permitir. `
        + 'Se ela ja tiver sido postada, o cancelamento e recusado e a nova etiqueta e cobrada a parte.',
      confirmText: 'Criar nova etiqueta',
      confirmColor: 'orange',
    });
    if (!ok) return;
    await handlePrepararDados(true);
  };

  const handleGerarEtiqueta = async (servicoId, semNfe = false) => {
    if (!isConferido) {
      avisarConferenciaPendente();
      return;
    }
    setGerandoId(servicoId);
    setUltimoServicoId(servicoId);
    setErrorMsg("");
    setNfeEmUso(false);

    const payloadFormatado = syncPayloadForApi();

    try {
      const response = await api.post('/melhor-envio/processar-completo', {
        venda_id: vendaId,
        servico_id: servicoId,
        custom_payload: payloadFormatado,
        reenvio: modoReenvio,
        sem_nfe: modoReenvio && semNfe === true
      });

      toast({ title: 'Sucesso!', description: 'Etiqueta comprada e gerada.', status: 'success', isClosable: true, position: 'top-right' });

      if (response.data.url) {
        window.open(response.data.url, '_blank');
      }

      await handlePrepararDados();

    } catch (error) {
      // A ME recusa a mesma chave enquanto o envio anterior estiver ativo: oferece a saida
      // de gerar sem a chave, com o DANFE original seguindo na caixa.
      setNfeEmUso(!!error.response?.data?.nfe_em_uso);
      setErrorMsg(error.response?.data?.error || 'Erro ao gerar etiqueta.');
    } finally {
      setGerandoId(null);
    }
  };

  const handleAdicionarCarrinho = async (servicoId) => {
    if (!isConferido) {
      avisarConferenciaPendente();
      return;
    }
    setAdicionandoCarrinhoId(servicoId);
    setErrorMsg("");

    const payloadFormatado = syncPayloadForApi();

    try {
      await api.post('/melhor-envio/adicionar-carrinho', {
        venda_id: vendaId,
        servico_id: servicoId,
        custom_payload: payloadFormatado
      });
      toast({ title: 'Adicionado ao carrinho!', description: 'Acesse o Carrinho no painel para finalizar o pagamento.', status: 'success', isClosable: true, position: 'top-right' });
    } catch (error) {
      setErrorMsg(error.response?.data?.error || 'Erro ao adicionar ao carrinho.');
    } finally {
      setAdicionandoCarrinhoId(null);
    }
  };

  // Baixa o arquivo binário da etiqueta (PDF ou ZPL) e devolve como Blob
  const baixarArquivoEtiqueta = async (format) => {
    const response = await api.get('/melhor-envio/imprimir-arquivo', {
      params: { order: etiquetaSalva.envio_externo_id, format },
      responseType: 'blob',
    });

    const contentType = response.headers['content-type'] || '';

    // Se o backend retornou JSON, é erro — ler texto e propagar
    if (contentType.includes('application/json')) {
      const text = await response.data.text();
      try {
        const json = JSON.parse(text);
        throw new Error(json.error || 'Erro ao baixar etiqueta.');
      } catch (e) {
        throw new Error(e.message || 'Erro ao baixar etiqueta.');
      }
    }

    return response.data;
  };

  const triggerDownload = (blob, mimeType, fileName) => {
    const url = URL.createObjectURL(new Blob([blob], { type: mimeType }));
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleBaixarPdf = async () => {
    setIsActionLoading(true);
    try {
      const blob = await baixarArquivoEtiqueta('pdf');
      triggerDownload(blob, 'application/pdf', `etiqueta_${etiquetaSalva.envio_externo_id}.pdf`);
      toast({ title: 'PDF baixado com sucesso!', status: 'success', duration: 2500, isClosable: true });
    } catch (error) {
      toast({ title: 'Erro ao baixar PDF', description: error.message || 'Falha ao baixar arquivo.', status: 'error', isClosable: true });
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleBaixarZpl = async () => {
    setIsActionLoading(true);
    try {
      const blob = await baixarArquivoEtiqueta('zpl');
      triggerDownload(blob, 'text/plain', `etiqueta_${etiquetaSalva.envio_externo_id}.zpl`);
      toast({ title: 'ZPL baixado com sucesso!', status: 'success', duration: 2500, isClosable: true });
    } catch (error) {
      toast({ title: 'Erro ao baixar ZPL', description: error.message || 'Falha ao baixar arquivo.', status: 'error', isClosable: true });
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleCopiarZpl = async () => {
    setIsActionLoading(true);
    try {
      const blob = await baixarArquivoEtiqueta('zpl');
      const text = await blob.text();
      await navigator.clipboard.writeText(text);
      toast({ title: 'ZPL copiado!', description: 'Conteúdo copiado para a área de transferência.', status: 'success', duration: 2500, isClosable: true });
    } catch (error) {
      toast({ title: 'Erro ao copiar ZPL', description: error.message || 'Falha ao copiar.', status: 'error', isClosable: true });
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleRemoverDoCarrinho = async () => {
    if (!etiquetaSalva?.envio_externo_id) return;
    setIsActionLoading(true);
    try {
      await api.delete(`/melhor-envio/carrinho/${etiquetaSalva.envio_externo_id}`);
      toast({
        title: 'Removido do carrinho',
        description: 'A etiqueta foi removida do carrinho Melhor Envio.',
        status: 'success', isClosable: true, position: 'top-right'
      });
      setEtiquetaSalva(null);
      handleCloseModal();
    } catch (error) {
      toast({
        title: 'Erro ao remover',
        description: error.response?.data?.error || 'Falha ao remover do carrinho.',
        status: 'error', isClosable: true
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  const handlePagarCarrinhoItem = async () => {
    if (!etiquetaSalva?.envio_externo_id) return;
    setIsActionLoading(true);
    try {
      // Faz checkout do item específico
      await api.post('/melhor-envio/checkout', { orders: [etiquetaSalva.envio_externo_id] });
      // Gera etiqueta na transportadora
      await api.post('/melhor-envio/gerar', { orders: [etiquetaSalva.envio_externo_id] });
      toast({
        title: 'Etiqueta paga e gerada!',
        status: 'success', isClosable: true, position: 'top-right'
      });
      // Recarrega os dados da etiqueta
      await handleAtualizarRastreio();
    } catch (error) {
      toast({
        title: 'Erro ao pagar',
        description: error.response?.data?.error || 'Falha ao processar pagamento.',
        status: 'error', isClosable: true
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleAtualizarRastreio = async () => {
    setIsActionLoading(true);
    try {
      await api.post('/melhor-envio/rastrear', { orders: [etiquetaSalva.envio_externo_id] });
      toast({ title: 'Rastreio Atualizado!', status: 'success', isClosable: true });
      await handlePrepararDados();
    } catch (error) {
      toast({ title: 'Erro', description: 'Falha ao buscar rastreio na transportadora.', status: 'error', isClosable: true });
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleToChange = (field, value) => {
    if (field === 'document') {
      const numbers = value.replace(/\D/g, '');

      if (numbers.length <= 11) {
        setPayloadObj(prev => ({
          ...prev,
          to: {
            ...prev.to,
            document: numbers,
            company_document: ''
          }
        }));
      } else {
        setPayloadObj(prev => ({
          ...prev,
          to: {
            ...prev.to,
            document: '',
            company_document: numbers
          }
        }));
      }
      return;
    }

    setPayloadObj(prev => ({
      ...prev,
      to: { ...prev.to, [field]: value }
    }));
  };

  const LogoMelhorEnvio = () => (
    <Image
      src="/melhor_envio_logo.webp"
      alt="Melhor Envio"
      h="20px"
      w="auto"
      objectFit="contain"
    />
  );

  const verificarDestaque = (cotacao) => {
    if (!dbFull) return false;
    const apiEmpresa = (cotacao.company?.name || '').toLowerCase().replace(/[^a-z0-9]/gi, '');
    const apiServico = (cotacao.name || '').toLowerCase().replace(/[^a-z0-9]/gi, '');

    if (!apiEmpresa || !dbFull.includes(apiEmpresa)) return false;

    if (apiServico && dbFull.includes(apiServico)) return true;

    if (dbFull === apiEmpresa) return true;

    return false;
  };

  const bannerReenvio = modoReenvio ? (
    <Alert status="warning" borderRadius="md" mb={3} variant="left-accent" alignItems="flex-start">
      <AlertIcon />
      <Box>
        <Text fontWeight="bold" fontSize="sm">Reenvio desta venda</Text>
        <Text fontSize="xs">
          Esta venda ja teve etiqueta. A anterior e cancelada se o Melhor Envio permitir, e a nova etiqueta
          e cobrada do saldo normalmente. As medidas ja vem da caixa do envio anterior: confira se a
          embalagem do reenvio for outra.
        </Text>
      </Box>
    </Alert>
  ) : null;

  const temFreteOriginal = !!(dbEmpresa || dbServico);
  const bannerFreteOriginal = temFreteOriginal ? (
    <Box
      mb={3}
      px={3}
      py={2}
      bgGradient="linear(to-r, blue.500, blue.600)"
      borderRadius="md"
      boxShadow="md"
      color="white"
    >
      <Flex align="center" gap={2.5}>
        <Icon as={FaTruck} color="white" boxSize={4} flexShrink={0} />
        <Text fontSize="xs" color="blue.100" fontWeight="bold" textTransform="uppercase" letterSpacing="wider" flexShrink={0}>
          Frete do cliente:
        </Text>
        <Text fontSize="sm" fontWeight="extrabold" color="white" noOfLines={1}>
          {dbEmpresa || 'Empresa não informada'}
          {dbServico && <Text as="span" color="blue.100"> — {dbServico}</Text>}
        </Text>
      </Flex>
    </Box>
  ) : (
    <Box
      mb={3}
      px={3}
      py={2}
      bgGradient="linear(to-r, orange.400, orange.500)"
      borderRadius="md"
      boxShadow="md"
      color="white"
    >
      <Flex align="center" gap={2.5}>
        <Icon as={FaExclamationCircle} color="white" boxSize={4} flexShrink={0} />
        <Text fontSize="xs" color="orange.100" fontWeight="bold" textTransform="uppercase" letterSpacing="wider" flexShrink={0}>
          Frete do cliente:
        </Text>
        <Text fontSize="sm" color="white" fontStyle="italic" noOfLines={1}>
          Não informado pelo marketplace
        </Text>
      </Flex>
    </Box>
  );

  return (
    <>
      {!autoReenvio && (
      <Tooltip
        label={
          isNoCarrinho
            ? "No carrinho — aguardando pagamento"
            : isGerada
              ? "Ver Etiqueta Gerada"
              : !isConferido
                ? "Venda nao conferida - conclua a conferencia antes de gerar etiqueta"
                : "Cotar e Gerar etiqueta via Melhor Envio"
        }
        placement="top"
        isDisabled={isMobile}
        hasArrow
      >
        <Button
          h="30px"
          px={2}
          fontSize="xs"
          fontWeight="semibold"
          border="1px solid"
          borderColor={isNoCarrinho ? "yellow.400" : (isGerada ? "green.400" : (!isConferido ? "gray.300" : "blue.300"))}
          bg={isNoCarrinho ? "yellow.50" : (isGerada ? "green.50" : "white")}
          color={isNoCarrinho ? "yellow.800" : (isGerada ? "green.700" : (!isConferido ? "gray.400" : "blue.600"))}
          opacity={isNoCarrinho || isGerada || isConferido ? 1 : 0.55}
          cursor={isNoCarrinho || isGerada || isConferido ? "pointer" : "not-allowed"}
          _hover={
            isNoCarrinho
              ? { bg: "yellow.100", borderColor: "yellow.500" }
              : isGerada
                ? { bg: "green.100", borderColor: "green.500" }
                : isConferido
                  ? { bg: "gray.50", borderColor: "blue.400" }
                  : {}
          }
          isLoading={isLoading && step === null}
          onClick={() => handlePrepararDados()}
          display="flex"
          alignItems="center"
          gap={1}
          shadow="sm"
        >
          {isNoCarrinho ? (
            <>
              <Icon as={FaShoppingCart} color="yellow.600" />
              <LogoMelhorEnvio />
            </>
          ) : isGerada ? (
            <>
              <Icon as={FaCheckCircle} color="green.500" />
              <LogoMelhorEnvio />
            </>
          ) : (
            <LogoMelhorEnvio />
          )}
        </Button>
      </Tooltip>
      )}
      {!autoReenvio && comTexto && (
        <Text fontSize="11px" fontWeight="600" mt={1} color={isNoCarrinho ? "yellow.700" : isGerada ? "green.600" : !isConferido ? "gray.500" : "blue.600"}>
          {isNoCarrinho ? "No carrinho" : isGerada ? "Etiqueta gerada" : !isConferido ? "Aguardando conferência" : "Gerar etiqueta"}
        </Text>
      )}

      <Modal isOpen={isOpen} onClose={handleCloseModal} size={{ base: "full", md: step === 'printed' ? '2xl' : '3xl', lg: step === 'printed' ? '2xl' : '5xl' }}>
        <ModalOverlay backdropFilter="blur(3px)" />
        <ModalContent>
          <ModalHeader bg="gray.50" borderBottomWidth="1px">
            {step === 'review' && 'Revisar Dados do Envio'}
            {step === 'quotes' && 'Escolha o Frete'}
            {step === 'printed' && 'Gerenciar Envio'}
          </ModalHeader>
          <ModalCloseButton mt={1} isDisabled={gerandoId !== null || isLoading || isActionLoading} />

          <ModalBody py={{ base: 4, md: 6 }} bg={{ base: "gray.50", md: "gray.50" }}>

            {errorMsg && (
              <Alert status="error" mb={5} borderRadius="md" alignItems="flex-start">
                <AlertIcon />
                <Box>
                  <Text fontSize="sm">{errorMsg}</Text>
                  {nfeEmUso && modoReenvio && (
                    <>
                      <Text fontSize="xs" mt={2} color="red.700">
                        O Melhor Envio so libera a mesma chave depois que o envio anterior sai do ar, e ele nao pode ser
                        cancelado. Gerando sem a chave, a etiqueta sai como declaracao de conteudo: transportadora como a
                        Total Express recusa. Envie o DANFE original dentro da caixa.
                      </Text>
                      <Button
                        mt={2}
                        size="sm"
                        colorScheme="orange"
                        borderRadius="lg"
                        isLoading={gerandoId !== null}
                        onClick={() => handleGerarEtiqueta(ultimoServicoId, true)}
                      >
                        Gerar sem a chave da NF-e
                      </Button>
                    </>
                  )}
                </Box>
              </Alert>
            )}

            {step === 'printed' && etiquetaSalva && (() => {
              // Detecta se a etiqueta está APENAS no carrinho (criada mas não paga/gerada)
              const isNoCarrinho = etiquetaSalva.status === 'pending' && !etiquetaSalva.paid_at && !etiquetaSalva.generated_at;
              const scheme = isNoCarrinho ? 'yellow' : 'green';
              const title = isNoCarrinho ? 'Etiqueta no Carrinho' : 'Etiqueta Gerada com Sucesso';
              const subtitle = isNoCarrinho
                ? 'Esta etiqueta ainda não foi paga. Finalize o pagamento ou remova do carrinho.'
                : 'A etiqueta já está disponível para impressão';
              const IconComp = isNoCarrinho ? FaShoppingCart : FaCheckCircle;

              return (
              <Box bg="white" borderRadius="2xl" shadow="sm" border="1px solid" borderColor={`${scheme}.200`} overflow="hidden">
                {/* Header colorido conforme estado */}
                <Box bg={`${scheme}.50`} px={6} py={6} borderBottom="1px solid" borderColor={`${scheme}.100`} textAlign="center">
                  <Flex justify="center" mb={3}>
                    <Flex w="64px" h="64px" bg={`${scheme}.500`} borderRadius="full" align="center" justify="center" boxShadow="md">
                      <Icon as={IconComp} boxSize={8} color="white" />
                    </Flex>
                  </Flex>
                  <Text fontSize="xl" fontWeight="extrabold" color={`${scheme}.700`}>{title}</Text>
                  <Text fontSize="sm" color={`${scheme}.600`} mt={1}>{subtitle}</Text>
                </Box>

                <Box px={{ base: 4, md: 6 }} py={5}>
                  {/* Aviso se está só no carrinho */}
                  {isNoCarrinho && (
                    <Alert status="warning" borderRadius="xl" mb={4} variant="left-accent">
                      <AlertIcon />
                      <Box>
                        <Text fontWeight="bold" fontSize="sm">Aguardando pagamento</Text>
                        <Text fontSize="xs">
                          A etiqueta foi reservada no carrinho do Melhor Envio mas o saldo ainda não foi debitado.
                          Você pode finalizar agora ou remover para liberar a cotação.
                        </Text>
                      </Box>
                    </Alert>
                  )}

                  {/* Código de rastreio em destaque (só se já gerada) */}
                  {!isNoCarrinho && (
                    <Box bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="xl" p={4} mb={4}>
                      <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold" letterSpacing="wider" mb={1}>
                        Código de Rastreio
                      </Text>
                      {etiquetaSalva.codigo_rastreio ? (
                        <Flex align="center" gap={2} wrap="wrap">
                          <Link
                            href={`https://melhorrastreio.com.br/rastreio/${etiquetaSalva.codigo_rastreio}`}
                            isExternal
                            _hover={{ textDecor: 'none' }}
                            flex="1"
                            minW={0}
                          >
                            <Flex
                              align="center"
                              gap={2}
                              bg="blue.50"
                              color="blue.700"
                              px={3}
                              py={2}
                              borderRadius="md"
                              _hover={{ bg: 'blue.100' }}
                              transition="background 0.15s"
                            >
                              <Text
                                fontWeight="black"
                                fontSize={{ base: "lg", md: "2xl" }}
                                fontFamily="mono"
                                wordBreak="break-all"
                                flex="1"
                                minW={0}
                              >
                                {etiquetaSalva.codigo_rastreio}
                              </Text>
                              <Icon as={ExternalLinkIcon} boxSize={4} flexShrink={0} />
                            </Flex>
                          </Link>
                          <Tooltip label="Copiar código" hasArrow>
                            <IconButton
                              icon={<FaCopy />}
                              size="sm"
                              variant="ghost"
                              colorScheme="blue"
                              borderRadius="md"
                              aria-label="Copiar código de rastreio"
                              flexShrink={0}
                              onClick={() => {
                                navigator.clipboard.writeText(etiquetaSalva.codigo_rastreio);
                                toast({ title: 'Código copiado!', status: 'success', duration: 2000, isClosable: true });
                              }}
                            />
                          </Tooltip>
                        </Flex>
                      ) : (
                        <Text fontWeight="black" fontSize="2xl" fontFamily="mono" color="gray.300">
                          Aguardando transportadora...
                        </Text>
                      )}
                    </Box>
                  )}

                  {/* Grid de informações */}
                  <Grid templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }} gap={3} mb={5}>
                    <Box bg="gray.50" borderRadius="xl" p={3}>
                      <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold">Status</Text>
                      <Badge colorScheme={isNoCarrinho ? 'yellow' : (etiquetaSalva.status === 'released' ? 'green' : 'blue')}
                        fontSize="xs" mt={1.5} px={2} py={0.5} borderRadius="full">
                        {isNoCarrinho ? 'No Carrinho' : (etiquetaSalva.status || 'Pendente')}
                      </Badge>
                    </Box>
                    <Box bg="gray.50" borderRadius="xl" p={3}>
                      <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold">Custo</Text>
                      <Text fontWeight="black" fontSize="md" color="green.600" mt={0.5}>
                        R$ {parseFloat(etiquetaSalva.preco || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </Text>
                    </Box>
                    <Box bg="gray.50" borderRadius="xl" p={3} gridColumn={{ base: "auto", sm: "1 / -1", md: "auto" }}>
                      <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold">Protocolo</Text>
                      <Text fontSize="xs" color="gray.700" mt={1} fontFamily="mono" wordBreak="break-all">
                        {etiquetaSalva.protocolo || 'N/A'}
                      </Text>
                    </Box>
                    <Box bg="gray.50" borderRadius="xl" p={3} gridColumn={{ base: "auto", sm: "1 / -1", md: "auto" }}>
                      <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold">ID Externo</Text>
                      <Text fontSize="xs" color="gray.700" mt={1} fontFamily="mono" wordBreak="break-all">
                        {etiquetaSalva.envio_externo_id}
                      </Text>
                    </Box>
                  </Grid>

                  {/* Botões de ação */}
                  {isNoCarrinho ? (
                    <Flex gap={3} direction={{ base: "column", md: "row" }}>
                      <Button colorScheme="green" leftIcon={<FaCheck />} borderRadius="xl" size="md"
                        onClick={handlePagarCarrinhoItem} isLoading={isActionLoading} flex={1}>
                        Pagar e Gerar Agora
                      </Button>
                      <Button colorScheme="red" variant="outline" leftIcon={<FaTrash />} borderRadius="xl" size="md"
                        onClick={handleRemoverDoCarrinho} isLoading={isActionLoading} flex={1}>
                        Remover do Carrinho
                      </Button>
                    </Flex>
                  ) : (
                    (() => {
                      const btnProps = {
                        size: "md",
                        borderRadius: "xl",
                        w: "100%",
                        minW: 0,
                        h: "auto",
                        py: 2.5,
                        px: 3,
                        whiteSpace: "normal",
                        textAlign: "center",
                        wordBreak: "break-word",
                        fontSize: { base: "sm", md: "sm" },
                        isLoading: isActionLoading,
                      };
                      const precisaReenvio = STATUS_PERMITE_REENVIO.includes(etiquetaSalva.status);
                      return (
                        <>
                          {precisaReenvio && (
                            <Alert status="warning" borderRadius="xl" mb={4} variant="left-accent" alignItems="flex-start">
                              <AlertIcon />
                              <Box>
                                <Text fontWeight="bold" fontSize="sm">Precisa enviar de novo?</Text>
                                <Text fontSize="xs">
                                  Esta etiqueta esta como "{etiquetaSalva.status}". Se a peca voltou ou nao foi entregue,
                                  gere outra etiqueta para a mesma venda.
                                </Text>
                              </Box>
                            </Alert>
                          )}
                          <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={3}>
                            <Button {...btnProps} colorScheme="blue" leftIcon={<FaFilePdf />} onClick={handleBaixarPdf}>
                              Baixar PDF
                            </Button>
                            <Button {...btnProps} colorScheme="purple" leftIcon={<FaBarcode />} onClick={handleBaixarZpl}>
                              Baixar ZPL
                            </Button>
                            <Button {...btnProps} colorScheme="purple" variant="outline" leftIcon={<FaCopy />} onClick={handleCopiarZpl}>
                              Copiar ZPL
                            </Button>
                            <Button {...btnProps} variant="outline" colorScheme="gray" leftIcon={<FaSyncAlt />} onClick={handleAtualizarRastreio}>
                              Atualizar Rastreio
                            </Button>
                          </SimpleGrid>
                          <Button
                            mt={3}
                            w="100%"
                            size="md"
                            borderRadius="xl"
                            colorScheme="orange"
                            variant={precisaReenvio ? 'solid' : 'outline'}
                            leftIcon={<FaTruck />}
                            isLoading={isLoading}
                            onClick={handleAbrirReenvio}
                          >
                            Criar Nova Etiqueta (reenvio)
                          </Button>
                        </>
                      );
                    })()
                  )}
                </Box>
              </Box>
              );
            })()}

            {step === 'review' && payloadObj && (
              <Box bg="white" p={{ base: 4, md: 6 }} borderRadius="md" shadow="sm" border="1px solid" borderColor="gray.200">
                {bannerReenvio}
                {bannerFreteOriginal}

                <Text mb={4} fontWeight="bold" color="blue.600" fontSize="lg">1. Dimensões do Pacote (Volumes)</Text>

                {payloadObj.volumes.map((vol, idx) => (
                  <Box key={idx} mb={3} p={3} bg="gray.50" borderRadius="md" border="1px dashed" borderColor="gray.300">
                    <Flex direction={{ base: "column", sm: "row" }} justify="space-between" align={{ base: "stretch", sm: "center" }} mb={3} gap={2}>
                      <FormControl w="full" maxW={{ base: "full", sm: "300px" }}>
                        <Input size="sm" type="text" bg="white" value={vol._nome || ''} onChange={(e) => handleVolumeChange(idx, '_nome', e.target.value)} placeholder="Nome do pacote" fontWeight="bold" />
                      </FormControl>
                      {payloadObj.volumes.length > 1 && (
                        <Button size="xs" colorScheme="red" variant="ghost" alignSelf={{ base: "flex-end", sm: "auto" }} onClick={() => handleRemoveVolume(idx)}>
                          Remover
                        </Button>
                      )}
                    </Flex>
                    <Grid templateColumns={{ base: "repeat(2, 1fr)", md: "repeat(5, 1fr)" }} gap={4}>
                      <GridItem>
                        <FormControl>
                          <FormLabel fontSize="xs" mb={1}>Altura (cm)</FormLabel>
                          <Input size="sm" type="text" bg="white" value={vol.height} onChange={(e) => handleVolumeChange(idx, 'height', e.target.value)} />
                        </FormControl>
                      </GridItem>
                      <GridItem>
                        <FormControl>
                          <FormLabel fontSize="xs" mb={1}>Largura (cm)</FormLabel>
                          <Input size="sm" type="text" bg="white" value={vol.width} onChange={(e) => handleVolumeChange(idx, 'width', e.target.value)} />
                        </FormControl>
                      </GridItem>
                      <GridItem>
                        <FormControl>
                          <FormLabel fontSize="xs" mb={1}>Comprim. (cm)</FormLabel>
                          <Input size="sm" type="text" bg="white" value={vol.length} onChange={(e) => handleVolumeChange(idx, 'length', e.target.value)} />
                        </FormControl>
                      </GridItem>
                      <GridItem>
                        <FormControl>
                          <FormLabel fontSize="xs" mb={1}>Peso (kg)</FormLabel>
                          <Input size="sm" type="text" bg="white" value={vol.weight} onChange={(e) => handleVolumeChange(idx, 'weight', e.target.value)} />
                        </FormControl>
                      </GridItem>
                      <GridItem>
                        <FormControl>
                          <FormLabel fontSize="xs" mb={1}>Seguro (R$)</FormLabel>
                          <Input size="sm" type="text" bg="white" value={vol._valor || ''} onChange={(e) => handleVolumeChange(idx, '_valor', e.target.value)} />
                        </FormControl>
                      </GridItem>
                    </Grid>

                    {vol._estimado && (
                      <Text mt={2} fontSize="xs" color="gray.500">
                        <Text as="span" fontWeight="bold" color="gray.600">Dimensões estimadas:</Text>{' '}
                        Altura {formatarEstimativa(vol._estimado.height)} · Largura {formatarEstimativa(vol._estimado.width)} · Comp. {formatarEstimativa(vol._estimado.length)} · Peso {formatarEstimativa(vol._estimado.weight)} kg
                      </Text>
                    )}
                  </Box>
                ))}

                <Flex justify="space-between" align="center" mt={2} mb={4} direction={{ base: "column", sm: "row" }} gap={2}>
                  <Button size="sm" colorScheme="blue" variant="outline" w={{ base: "full", sm: "auto" }} onClick={handleAddVolume}>
                    + Adicionar Outro Pacote
                  </Button>
                  <Button size="sm" colorScheme="green" leftIcon={<FaSave />} w={{ base: "full", sm: "auto" }} isLoading={salvandoDimensoes} onClick={handleSalvarDimensoes}>
                    Salvar dimensões
                  </Button>
                </Flex>

                {entregaConfirmadaManual && (
                  <Flex mt={2} mb={2} px={4} py={3} borderRadius="md" bg="green.50" border="1px solid" borderColor="green.200" align="center" gap={2}>
                    <Icon as={FaCheckCircle} color="green.500" />
                    <Text fontSize="sm" color="green.800" fontWeight="bold">
                      Dados de entrega confirmados pela equipe
                    </Text>
                  </Flex>
                )}

                {transportadoraCombinada && (
                  <Box mt={2} mb={2} px={4} py={3} borderRadius="md" bg="orange.50" border="1px solid" borderColor="orange.200">
                    <Text fontSize="sm" color="orange.800">
                      <b>Transportadora combinada com o cliente:</b> {transportadoraCombinada}
                    </Text>
                  </Box>
                )}

                <Box mt={4} borderRadius="md" border="1px solid" borderColor="gray.200" overflow="hidden">
                  <Flex
                    as="button"
                    type="button"
                    w="100%"
                    px={4}
                    py={3}
                    align="center"
                    justify="space-between"
                    cursor="pointer"
                    onClick={() => setShowDestinatarioReview(prev => !prev)}
                    _hover={{ bg: 'gray.50' }}
                    bg={showDestinatarioReview ? 'gray.50' : 'white'}
                    transition="background 0.15s"
                  >
                    <HStack spacing={3}>
                      <Icon as={FaUser} color="gray.500" />
                      <Text fontWeight="bold" fontSize="md" color="gray.700">Editar Endereço do Destinatário</Text>
                      {!showDestinatarioReview && (
                        <Badge colorScheme="gray" variant="subtle" fontSize="0.65rem">opcional</Badge>
                      )}
                    </HStack>
                    <Icon as={showDestinatarioReview ? ChevronUpIcon : ChevronDownIcon} boxSize={5} color="gray.500" />
                  </Flex>

                  <Collapse in={showDestinatarioReview} animateOpacity>
                    <Box px={4} pb={4} pt={2} borderTop="1px solid" borderTopColor="gray.100" bg="white">
                      <Grid templateColumns={{ base: "1fr", md: "repeat(12, 1fr)" }} gap={4}>
                        <GridItem colSpan={{ base: 12, md: 8 }}>
                          <FormControl isRequired>
                            <FormLabel fontSize="xs" color="gray.600">Nome do Cliente</FormLabel>
                            <Input size="sm" value={payloadObj.to?.name || ''} onChange={(e) => handleToChange('name', e.target.value)} />
                          </FormControl>
                        </GridItem>
                        <GridItem colSpan={{ base: 12, md: 4 }}>
                          <FormControl isRequired>
                            <FormLabel fontSize="xs" color="gray.600">CEP</FormLabel>
                            <Input size="sm" value={payloadObj.to?.postal_code || ''} onChange={(e) => handleToChange('postal_code', e.target.value)} />
                          </FormControl>
                        </GridItem>
                        <GridItem colSpan={{ base: 12, md: 6 }}>
                          <FormControl isRequired>
                            <FormLabel fontSize="xs" color="gray.600">CPF / CNPJ</FormLabel>
                            <Input
                              size="sm"
                              value={payloadObj.to?.document || payloadObj.to?.company_document || ''}
                              onChange={(e) => handleToChange('document', e.target.value)}
                            />
                          </FormControl>
                        </GridItem>
                        <GridItem colSpan={{ base: 12, md: 6 }}>
                          <FormControl isRequired>
                            <FormLabel fontSize="xs" color="gray.600">Telefone (Somente DDD+Num)</FormLabel>
                            <Input size="sm" value={payloadObj.to?.phone || ''} onChange={(e) => handleToChange('phone', e.target.value)} />
                          </FormControl>
                        </GridItem>
                        <GridItem colSpan={{ base: 12, md: 9 }}>
                          <FormControl isRequired>
                            <FormLabel fontSize="xs" color="gray.600">Rua / Endereço</FormLabel>
                            <Input size="sm" value={payloadObj.to?.address || ''} onChange={(e) => handleToChange('address', e.target.value)} />
                          </FormControl>
                        </GridItem>
                        <GridItem colSpan={{ base: 12, md: 3 }}>
                          <FormControl isRequired>
                            <FormLabel fontSize="xs" color="gray.600">Número</FormLabel>
                            <Input size="sm" value={payloadObj.to?.number || ''} onChange={(e) => handleToChange('number', e.target.value)} />
                          </FormControl>
                        </GridItem>
                        <GridItem colSpan={{ base: 12, md: 5 }}>
                          <FormControl>
                            <FormLabel fontSize="xs" color="gray.600">Complemento</FormLabel>
                            <Input size="sm" value={payloadObj.to?.complement || ''} onChange={(e) => handleToChange('complement', e.target.value)} />
                          </FormControl>
                        </GridItem>
                        <GridItem colSpan={{ base: 12, md: 4 }}>
                          <FormControl isRequired>
                            <FormLabel fontSize="xs" color="gray.600">Bairro</FormLabel>
                            <Input size="sm" value={payloadObj.to?.district || ''} onChange={(e) => handleToChange('district', e.target.value)} />
                          </FormControl>
                        </GridItem>
                        <GridItem colSpan={{ base: 12, md: 2 }}>
                          <FormControl isRequired>
                            <FormLabel fontSize="xs" color="gray.600">Cidade</FormLabel>
                            <Input size="sm" value={payloadObj.to?.city || ''} onChange={(e) => handleToChange('city', e.target.value)} />
                          </FormControl>
                        </GridItem>
                        <GridItem colSpan={{ base: 12, md: 1 }}>
                          <FormControl isRequired>
                            <FormLabel fontSize="xs" color="gray.600">UF</FormLabel>
                            <Input size="sm" maxLength={2} value={payloadObj.to?.state_abbr || ''} onChange={(e) => handleToChange('state_abbr', e.target.value.toUpperCase())} />
                          </FormControl>
                        </GridItem>
                      </Grid>
                    </Box>
                  </Collapse>
                </Box>

                <Flex mt={6} justify="flex-end">
                  <Button colorScheme="green" w={{ base: "full", md: "auto" }} size="md" leftIcon={<FaCheck />} onClick={handleCotarFrete} isLoading={isLoading}>
                    Confirmar e Cotar Frete
                  </Button>
                </Flex>
              </Box>
            )}

            {step === 'quotes' && (
              <>
                <Flex mb={4} justify="flex-start" align="center">
                  <Button size="sm" variant="outline" leftIcon={<FaArrowLeft />} onClick={() => setStep('review')} isDisabled={gerandoId !== null}>
                    Voltar e Editar Dados
                  </Button>
                </Flex>

                {payloadObj && (
                  <Box mb={6} p={4} bg="white" borderRadius="md" border="1px solid" borderColor="gray.200" shadow="sm">
                    <Text fontWeight="bold" color="gray.700" mb={3} fontSize="md">Resumo do Envio</Text>
                    <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={4}>
                      <GridItem>
                        <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase">Origem</Text>
                        <Text fontSize="sm" fontWeight="semibold" mt={1} isTruncated title={payloadObj.from?.name}>{payloadObj.from?.name}</Text>
                        {payloadObj.from?.company_document && <Text fontSize="xs" color="gray.600">CNPJ: {payloadObj.from?.company_document}</Text>}
                        <Text fontSize="xs" color="gray.600">{payloadObj.from?.address}, {payloadObj.from?.number} - {payloadObj.from?.district}</Text>
                        <Text fontSize="xs" color="gray.600">{payloadObj.from?.city} - {payloadObj.from?.state_abbr} | CEP: {payloadObj.from?.postal_code}</Text>
                        <Text fontSize="xs" color="gray.600">Tel: {payloadObj.from?.phone}</Text>
                      </GridItem>

                      <GridItem>
                        <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase">Destino</Text>
                        <Text fontSize="sm" fontWeight="semibold" mt={1} isTruncated title={payloadObj.to?.name}>{payloadObj.to?.name}</Text>
                        {(payloadObj.to?.document || payloadObj.to?.company_document) && (
                          <Text fontSize="xs" color="gray.600">Doc: {payloadObj.to?.document || payloadObj.to?.company_document}</Text>
                        )}
                        <Text fontSize="xs" color="gray.600">
                          {payloadObj.to?.address}, {payloadObj.to?.number} {payloadObj.to?.complement && `- ${payloadObj.to?.complement}`} - {payloadObj.to?.district}
                        </Text>
                        <Text fontSize="xs" color="gray.600">{payloadObj.to?.city} - {payloadObj.to?.state_abbr} | CEP: {payloadObj.to?.postal_code}</Text>
                        <Text fontSize="xs" color="gray.600">Tel: {payloadObj.to?.phone}</Text>
                      </GridItem>

                      <GridItem>
                        <Flex justify="space-between" align="center" mb={1} wrap="wrap" gap={2}>
                          <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase">Pacotes ({payloadObj.volumes?.length})</Text>
                          {payloadObj.options?.insurance_value > 0 && (
                            <Badge colorScheme="green" fontSize="0.6rem">
                              Valor Seguro: R$ {parseFloat(payloadObj.options.insurance_value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </Badge>
                          )}
                        </Flex>
                        <Box maxH="120px" overflowY="auto" pr={2}>
                          {payloadObj.volumes?.map((vol, idx) => (
                            <Text
                              key={idx}
                              fontSize="xs"
                              color="gray.600"
                              mb={2}
                              whiteSpace="normal"
                              wordBreak="break-word"
                            >
                              <Badge colorScheme="blue" mr={1} fontSize="0.6rem" verticalAlign="top">{idx + 1}</Badge>
                              {vol._nome || `Caixa ${idx + 1}`} - {vol.weight}kg ({vol.height}x{vol.width}x{vol.length}cm)
                            </Text>
                          ))}
                        </Box>
                      </GridItem>
                    </Grid>
                  </Box>
                )}

                {bannerReenvio}
                {bannerFreteOriginal}

                {cotacoes.length === 0 ? (
                  <Flex direction="column" align="center" justify="center" py={10} color="gray.500">
                    <Icon as={FaExclamationCircle} boxSize={10} mb={4} color="gray.400" />
                    <Text fontSize="lg">Nenhuma transportadora disponível para as dimensões/CEP informados.</Text>
                  </Flex>
                ) : (
                  <>
                    <Box display={{ base: "none", md: "block" }} overflowX="auto" borderRadius="xl" border="1px solid" borderColor="gray.200" bg="white">
                      <Table size="md" variant="simple">
                        <Thead>
                          <Tr bg="gray.50">
                            <Th py={3} color="gray.600" fontSize="xs" textTransform="uppercase" letterSpacing="wider">Transportadora</Th>
                            <Th color="gray.600" fontSize="xs" textTransform="uppercase" letterSpacing="wider">Serviço</Th>
                            <Th color="gray.600" fontSize="xs" textTransform="uppercase" letterSpacing="wider" isNumeric>Prazo</Th>
                            <Th color="gray.600" fontSize="xs" textTransform="uppercase" letterSpacing="wider" isNumeric>Valor</Th>
                            <Th color="gray.600" fontSize="xs" textTransform="uppercase" letterSpacing="wider" textAlign="center">Ação</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {(() => {
                            const idMaisBarato = cotacoes.length > 0
                              ? cotacoes.reduce((min, c) => parseFloat(c.price) < parseFloat(min.price) ? c : min, cotacoes[0]).id
                              : null;
                            return cotacoes.slice().sort((a, b) => {
                              const aDest = verificarDestaque(a);
                              const bDest = verificarDestaque(b);
                              if (aDest && !bDest) return -1;
                              if (!aDest && bDest) return 1;
                              return a.price - b.price;
                            }).map((cotacao) => {
                              const isDestacado = verificarDestaque(cotacao);
                              const isMaisBarato = cotacao.id === idMaisBarato;

                            return (
                              <Tr key={cotacao.id}
                                bg={isDestacado ? "green.50" : "transparent"}
                                _hover={{ bg: isDestacado ? "green.100" : "blue.50" }}
                                transition="background 0.15s"
                                borderLeft={isMaisBarato ? "3px solid" : "3px solid transparent"}
                                borderLeftColor={isMaisBarato ? "green.400" : "transparent"}>
                                <Td py={3}>
                                  <Flex align="center" gap={3}>
                                    {cotacao.company?.picture && (
                                      <Box bg="white" p={1.5} borderRadius="lg" border="1px solid" borderColor="gray.200">
                                        <Image src={cotacao.company.picture} alt={cotacao.company.name} w="50px" h="30px" objectFit="contain" />
                                      </Box>
                                    )}
                                    <Box>
                                      <Text fontWeight="bold" color="gray.800" whiteSpace="nowrap">{cotacao.company?.name}</Text>
                                      <Flex gap={1} mt={0.5}>
                                        {isDestacado && (
                                          <Badge colorScheme="green" variant="subtle" fontSize="0.6rem" borderRadius="full" px={2}>
                                            Original da Venda
                                          </Badge>
                                        )}
                                        {isMaisBarato && (
                                          <Badge colorScheme="yellow" variant="subtle" fontSize="0.6rem" borderRadius="full" px={2}>
                                            Mais Barato
                                          </Badge>
                                        )}
                                      </Flex>
                                    </Box>
                                  </Flex>
                                </Td>
                                <Td><Text fontWeight="semibold" color="gray.700" whiteSpace="nowrap">{cotacao.name}</Text></Td>
                                <Td isNumeric>
                                  <Badge colorScheme="purple" variant="subtle" px={2} py={1} borderRadius="full" fontSize="xs">
                                    {cotacao.delivery_time} {cotacao.delivery_time === 1 ? 'dia' : 'dias'}
                                  </Badge>
                                </Td>
                                <Td isNumeric>
                                  <Text fontWeight="black" color="green.600" fontSize="lg" whiteSpace="nowrap">
                                    R$ {parseFloat(cotacao.price).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                  </Text>
                                </Td>
                                <Td textAlign="center">
                                  <Flex gap={2} justify="center">
                                    <Button size="sm" colorScheme="green" leftIcon={<FaTruck />} borderRadius="lg"
                                      onClick={() => handleGerarEtiqueta(cotacao.id)}
                                      isLoading={gerandoId === cotacao.id}
                                      isDisabled={adicionandoCarrinhoId === cotacao.id}>
                                      Comprar
                                    </Button>
                                    <Button size="sm" colorScheme="blue" variant="outline" leftIcon={<FaShoppingCart />}
                                      borderRadius="lg"
                                      onClick={() => handleAdicionarCarrinho(cotacao.id)}
                                      isLoading={adicionandoCarrinhoId === cotacao.id}
                                      isDisabled={gerandoId === cotacao.id}>
                                      Carrinho
                                    </Button>
                                  </Flex>
                                </Td>
                              </Tr>
                            );
                          });
                          })()}
                        </Tbody>
                      </Table>
                    </Box>

                    <VStack display={{ base: "flex", md: "none" }} spacing={3} align="stretch">
                      {(() => {
                        const idMaisBaratoMobile = cotacoes.length > 0
                          ? cotacoes.reduce((min, c) => parseFloat(c.price) < parseFloat(min.price) ? c : min, cotacoes[0]).id
                          : null;
                        return cotacoes.slice().sort((a, b) => {
                          const aDest = verificarDestaque(a);
                          const bDest = verificarDestaque(b);
                          if (aDest && !bDest) return -1;
                          if (!aDest && bDest) return 1;
                          return a.price - b.price;
                        }).map((cotacao) => {
                          const isDestacado = verificarDestaque(cotacao);
                          const isMaisBarato = cotacao.id === idMaisBaratoMobile;

                        return (
                          <Box key={cotacao.id} p={4}
                            bg={isDestacado ? "green.50" : "white"}
                            borderRadius="xl"
                            borderWidth="1px"
                            borderColor={isDestacado ? "green.300" : "gray.200"}
                            borderLeft={isMaisBarato ? "4px solid" : "1px solid"}
                            borderLeftColor={isMaisBarato ? "green.400" : (isDestacado ? "green.300" : "gray.200")}
                            shadow="xs" _active={{ shadow: "sm" }}>
                            <Flex justify="space-between" align="flex-start" mb={3}>
                              <Flex align="center" gap={3} flex={1} minW={0}>
                                {cotacao.company?.picture && (
                                  <Box border="1px solid" borderColor="gray.100" p={1.5} borderRadius="lg" bg="white" flexShrink={0}>
                                    <Image src={cotacao.company.picture} alt={cotacao.company.name} w="40px" h="24px" objectFit="contain" />
                                  </Box>
                                )}
                                <Box minW={0}>
                                  <Text fontWeight="bold" color="gray.800" lineHeight="shorter" noOfLines={1}>
                                    {cotacao.company?.name}
                                  </Text>
                                  <Text fontSize="xs" color="gray.500" noOfLines={1}>{cotacao.name}</Text>
                                  <Flex gap={1} mt={1} flexWrap="wrap">
                                    {isMaisBarato && (
                                      <Badge colorScheme="yellow" variant="subtle" fontSize="0.6rem" borderRadius="full" px={2}>
                                        Mais Barato
                                      </Badge>
                                    )}
                                    {isDestacado && (
                                      <Badge colorScheme="green" variant="subtle" fontSize="0.6rem" borderRadius="full" px={2}>
                                        Original
                                      </Badge>
                                    )}
                                  </Flex>
                                </Box>
                              </Flex>
                              <Badge colorScheme="purple" variant="subtle" fontSize="xs" borderRadius="full" px={2} py={1} flexShrink={0}>
                                {cotacao.delivery_time} {cotacao.delivery_time === 1 ? 'dia' : 'dias'}
                              </Badge>
                            </Flex>
                            <Divider mb={3} borderColor="gray.100" />
                            <Flex justify="space-between" align="center" gap={2}>
                              <Text fontWeight="black" color="green.600" fontSize="xl" lineHeight="1">
                                R$ {parseFloat(cotacao.price).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                              </Text>
                              <Flex gap={2} flexShrink={0}>
                                <Button size="sm" colorScheme="green" leftIcon={<FaTruck />} borderRadius="lg"
                                  onClick={() => handleGerarEtiqueta(cotacao.id)}
                                  isLoading={gerandoId === cotacao.id}
                                  isDisabled={adicionandoCarrinhoId === cotacao.id}>
                                  Comprar
                                </Button>
                                <IconButton size="sm" colorScheme="blue" variant="outline" icon={<FaShoppingCart />}
                                  borderRadius="lg" aria-label="Adicionar ao carrinho"
                                  onClick={() => handleAdicionarCarrinho(cotacao.id)}
                                  isLoading={adicionandoCarrinhoId === cotacao.id}
                                  isDisabled={gerandoId === cotacao.id} />
                              </Flex>
                            </Flex>
                          </Box>
                        );
                        });
                      })()}
                    </VStack>
                  </>
                )}
              </>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
      <ConfirmPortal />
    </>
  );
};

export default GeradorEtiquetaMelhorEnvio;